import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyledInput } from 'shared/input';
import { useAutocompleteSuggestions } from './use-autocomplete-suggestions';
import { useFloating, autoUpdate, offset, flip, size, useInteractions, useRole, useDismiss, useListNavigation, useClick } from '@floating-ui/react';
import { Box } from 'shared/grid';
interface Props {
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
}
export const MapAutocomplete = ({
  onPlaceSelect
}: Props) => {
  const map = useMap();
  const places = useMapsLibrary('places');
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const {
    suggestions,
    resetSession,
    isLoading
  } = useAutocompleteSuggestions(inputValue, {
    locationBias: map.getCenter()
  });
  const listRef = useRef<Array<HTMLLIElement | null>>([]);

  // Setup Floating UI
  const {
    refs,
    floatingStyles,
    context
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    middleware: [offset(5), flip(), size({
      apply({
        rects,
        elements
      }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
          maxHeight: '300px'
        });
      }
    })],
    whileElementsMounted: autoUpdate
  });

  // We'll move this function declaration to avoid the reference error

  // Setup interactions
  const {
    getReferenceProps,
    getFloatingProps,
    getItemProps
  } = useInteractions([useRole(context, {
    role: 'listbox'
  }), useDismiss(context), useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    virtual: true,
    loop: true
  }), useClick(context)]);

  // Open dropdown when input is typed
  useEffect(() => {
    if (suggestions.length > 0 && inputValue) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [suggestions, inputValue]);
  const handleInput = useCallback(event => {
    setInputValue((event.target as HTMLInputElement).value);
    setActiveIndex(null);
  }, []);
  const handleSuggestionClick = useCallback(async (suggestion: google.maps.places.AutocompleteSuggestion) => {
    if (!places) return;
    if (!suggestion.placePrediction) return;
    const place = suggestion.placePrediction.toPlace();
    await place.fetchFields({
      fields: ['svgIconMaskURI', 'iconBackgroundColor', 'displayName', 'formattedAddress', 'location', 'viewport', 'addressComponents']
    });
    setInputValue('');
    setOpen(false);

    // calling fetchFields invalidates the session-token, so we now have to call
    // resetSession() so a new one gets created for further search
    resetSession();
    if (place?.viewport) {
      map.fitBounds(place.viewport);
    }
    onPlaceSelect(place);
  }, [places, resetSession, onPlaceSelect, map]);

  // Handle keyboard selection - now properly placed after handleSuggestionClick is declared
  const handleKeyDown = useCallback((event_0: React.KeyboardEvent) => {
    if (event_0.key === 'Enter' || event_0.key === 'Tab') {
      if (activeIndex !== null && suggestions[activeIndex]) {
        event_0.preventDefault();
        handleSuggestionClick(suggestions[activeIndex]);
        return;
      }
    } else if (event_0.key === 'ArrowDown') {
      event_0.preventDefault();
      if (!open) {
        setOpen(true);
        setActiveIndex(0);
        return;
      }

      // If dropdown is open, move to next item
      if (activeIndex === null) {
        setActiveIndex(0);
      } else {
        const nextIndex = (activeIndex + 1) % suggestions.length;
        setActiveIndex(nextIndex);
      }
    } else if (event_0.key === 'ArrowUp') {
      event_0.preventDefault();
      if (activeIndex === null) {
        setActiveIndex(suggestions.length - 1);
      } else {
        const prevIndex = (activeIndex - 1 + suggestions.length) % suggestions.length;
        setActiveIndex(prevIndex);
      }
    } else if (event_0.key === 'Escape') {
      setOpen(false);
      setActiveIndex(null);
      event_0.preventDefault();
    }
  }, [activeIndex, suggestions, handleSuggestionClick, open, setOpen]);
  return <Box className="autocomplete-container" data-sentry-element="Box" data-sentry-component="MapAutocomplete" data-sentry-source-file="autocomplete.tsx">
      <StyledInput ref={refs.setReference} placeholder="Search..." sx={{
      backgroundColor: 'background',
      width: '100%',
      textOverflow: 'ellipsis'
    }} value={inputValue} onInput={event_1 => handleInput(event_1)} onKeyDown={handleKeyDown} {...getReferenceProps({
      onKeyDown: event_2 => {
        // Prevent getReferenceProps from handling keys we want to handle ourselves
        const isHandledByUs = ['Enter', 'Tab', 'ArrowUp', 'ArrowDown', 'Escape'].includes(event_2.key);
        if (isHandledByUs) {
          handleKeyDown(event_2);
        }
        // Let other keys be handled normally
      }
    })} data-sentry-element="StyledInput" data-sentry-source-file="autocomplete.tsx" />

      {open && suggestions.length > 0 && <Box ref={refs.setFloating} style={{
      ...floatingStyles,
      overflowY: 'auto',
      padding: '8px 0',
      margin: 0,
      backgroundColor: 'white',
      borderRadius: '4px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    }} {...getFloatingProps({
      onKeyDown: handleKeyDown
    })} className="custom-list">
          {suggestions.map((suggestion_0, index) => {
        return <Box key={index} id={`suggestion-${index}`} ref={node => {
          listRef.current[index] = node;
        }} style={{
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: activeIndex === index ? '#f0f0f0' : 'transparent'
          //   outline: activeIndex === index ? '2px solid #4a90e2' : 'none',
        }} className="custom-list-item" role="option" aria-selected={activeIndex === index} tabIndex={-1} {...getItemProps({
          onClick: () => handleSuggestionClick(suggestion_0),
          onKeyDown: e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSuggestionClick(suggestion_0);
            }
          }
        })}>
                {suggestion_0.placePrediction?.text.text}
              </Box>;
      })}
        </Box>}
      {isLoading && <Box style={{
      padding: '8px'
    }}>Loading...</Box>}
    </Box>;
};